/**
 * 曝光事件参数采集器
 * 从 IntersectionObserverEntry 提取曝光信息
 * 对齐 docs/埋点数据.md event.expose 字段结构
 */

import type { ExposeEventParams } from '../types/event'
import { DATA_ATTRS } from '../core/constants'

/**
 * 曝光元素元数据
 */
export interface ExposeMeta {
  exposeEleId?: number
  exposeEleName?: string
  exposeTrigger?: string
}

/**
 * 从 IntersectionObserverEntry 中采集曝光参数
 * @param entry IntersectionObserverEntry 对象
 * @param meta 额外的元数据（可选）
 * @param exposeDuration 曝光持续时间（可选）
 * @param exposeTimes 曝光次数（可选）
 * @returns ExposeEventParams 对象
 */
export function collectExposeEvent(
  entry: IntersectionObserverEntry,
  meta?: ExposeMeta,
  exposeDuration?: number,
  exposeTimes?: number
): ExposeEventParams {
  const element = entry.target as HTMLElement
  const elementInfo = getElementInfo(element)

  return {
    exposeId: elementInfo.exposeId,
    exposeEleName: meta?.exposeEleName || elementInfo.eleName,
    exposeEleParams: elementInfo.eleParams,
    exposeRatio: Math.round(entry.intersectionRatio * 100) / 100,
    exposeDuration,
    exposeTimes,
    exposeTrigger: meta?.exposeTrigger || elementInfo.exposeTrigger,
    isClickAfter: false,
  }
}

/**
 * 从 HTML 元素直接采集曝光参数
 * @param element HTML 元素
 * @param meta 额外的元数据（可选）
 * @returns ExposeEventParams 对象
 */
export function collectExposeEventFromElement(
  element: HTMLElement,
  meta?: ExposeMeta
): ExposeEventParams {
  const elementInfo = getElementInfo(element)

  return {
    exposeId: elementInfo.exposeId,
    exposeEleName: meta?.exposeEleName || elementInfo.eleName,
    exposeEleParams: elementInfo.eleParams,
    exposeTrigger: meta?.exposeTrigger || elementInfo.exposeTrigger,
    isClickAfter: false,
  }
}

/**
 * 获取元素埋点信息
 */
export function getElementInfo(element: HTMLElement): { 
  eleId: string; 
  eleName: string; 
  eleParams: Record<string, unknown> | undefined; 
  listItemFilter: Record<string, unknown> | undefined;
  exposeId: number | undefined;
  exposeTrigger: string;
} {
  const eleId: string = element.getAttribute(DATA_ATTRS.ELE_ID) || ''
  const eleName: string = element.getAttribute(DATA_ATTRS.ELE_NAME) || ''

  // 解析元素绑定参数
  const eleParams = parseEleParams(element)

  // 解析元素LIST_ITEM_FILTER属性
  const listItemFilterRow = element.getAttribute(DATA_ATTRS.LIST_ITEM_FILTER)
  let listItemFilter: Record<string, unknown> | undefined
  try {
    listItemFilter = listItemFilterRow ? JSON.parse(listItemFilterRow) : undefined
  } catch {
    // 解析失败则跳过该节点
  }

  // 获取曝光ID
  const exposeIdRaw = element.getAttribute(DATA_ATTRS.EXPOSE_ID)
  const exposeId = exposeIdRaw ? Number(exposeIdRaw) : undefined

  // 获取曝光触发方式
  const exposeTrigger = element.getAttribute(DATA_ATTRS.EXPOSE_TRIGGER) || 'scroll'

  return { eleId, eleName, eleParams, listItemFilter, exposeId, exposeTrigger }
}


/**
 * 解析元素绑定参数
 * 从 data-tracker-ele-params 属性中解析 JSON 对象
 * data-tracker-ele-params 可能在目标元素或其父元素或祖先元素上定义
 * data-tracker-ele-params 可能一个或多个
 * data-tracker-ele-params 的值将被解析为 JSON 对象
 * 合并时子元素属性优先级高于父元素
 * 
 * 例如：
 * <div data-tracker-ele-params='{"a": 1, "b": 2}'>
  <span data-tracker-ele-params='{"b": 3, "c": 4}'>
    <button>Click</button>
  </span>
</div>
点击 button 时，最终合并结果为 { a: 1, b: 3, c: 4 }（子元素的 b: 3 覆盖了父元素的 b: 2）。
 */
function parseEleParams(element: HTMLElement): Record<string, unknown> | undefined {
  // 收集从目标元素到祖先元素的所有 ele-params
  const paramsList: Record<string, unknown>[] = []
  let current: HTMLElement | null = element

  while (current) {
    const raw = current.getAttribute(DATA_ATTRS.ELE_PARAMS)
    if (raw) {
      try {
        const parsed = JSON.parse(raw)
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          paramsList.push(parsed as Record<string, unknown>)
        }
      } catch {
        // 解析失败则跳过该节点
      }
    }
    current = current.parentElement
  }

  // 无有效参数则返回 undefined
  if (paramsList.length === 0) return undefined

  // 从后向前合并，确保子元素属性覆盖父元素属性
  // paramsList[0] 是目标元素，paramsList[n] 是最远祖先
  const merged: Record<string, unknown> = {}
  for (let i = paramsList.length - 1; i >= 0; i--) {
    Object.assign(merged, paramsList[i])
  }

  return merged
}

/**
 * 简单字符串 hash 转数值
 */
function simpleHash(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }
  return Math.abs(hash)
}
